import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'

const QUERY_KEY = patientId => ['FamilyMembers', patientId]
const LINKS_QUERY_KEY = patientId => ['FamilyMemberLinks', patientId]
const EMPTY_ARRAY = []

const parseApiError = async (res, fallback) => {
  try {
    const body = await res.json()
    if (typeof body?.errors === 'string') return body.errors
    if (body?.errors && typeof body.errors === 'object') {
      const [field, messages] = Object.entries(body.errors)[0] ?? []
      if (field && messages?.[0]) {
        return `${field.replace(/_/g, ' ')} ${messages[0]}`
      }
    }
  } catch {
    // fall through
  }
  return fallback
}

const toSnakePayload = ({
  fullName,
  phoneNumber,
  dateOfBirth,
  relatedPatientId,
  relationship,
  main,
  email,
  notes,
}) => {
  const payload = {}
  if (fullName !== undefined) payload.full_name = fullName
  if (phoneNumber !== undefined) payload.phone_number = phoneNumber
  if (dateOfBirth !== undefined) payload.date_of_birth = dateOfBirth
  if (relatedPatientId !== undefined) {
    payload.related_patient_id = relatedPatientId
  }
  if (relationship !== undefined) payload.relationship = relationship
  if (main !== undefined) payload.main = main
  if (email !== undefined) payload.email = email
  if (notes !== undefined) payload.notes = notes
  return payload
}

export const useFamilyMembers = patientId => {
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY(patientId),
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/family_members.json`,
        { headers }
      )
      if (!res.ok) {
        throw new Error("We couldn't load the family members")
      }
      return res.json()
    },
    enabled: !!patientId,
  })

  return { data: data ?? EMPTY_ARRAY, isLoading, isError }
}

export const useFamilyMemberLinks = patientId => {
  const { data, isLoading, isError } = useQuery({
    queryKey: LINKS_QUERY_KEY(patientId),
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/family_member_links.json`,
        { headers }
      )
      if (!res.ok) {
        throw new Error("We couldn't load the family member links")
      }
      return res.json()
    },
    enabled: !!patientId,
  })

  return { data: data ?? EMPTY_ARRAY, isLoading, isError }
}

export const useCreateFamilyMember = patientId => {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async input => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/family_members.json`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ family_member: toSnakePayload(input) }),
        }
      )
      if (!res.ok) {
        throw new Error(await parseApiError(res, 'Could not add family member'))
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(patientId) })
      queryClient.invalidateQueries({ queryKey: LINKS_QUERY_KEY(patientId) })
    },
  })

  return { mutateAsync, isPending }
}

export const useUpdateFamilyMember = patientId => {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({ id, ...input }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/family_members/${id}.json`,
        {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ family_member: toSnakePayload(input) }),
        }
      )
      if (!res.ok) {
        throw new Error(
          await parseApiError(res, 'Could not update family member')
        )
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(patientId) })
      queryClient.invalidateQueries({ queryKey: LINKS_QUERY_KEY(patientId) })
    },
  })

  return { mutateAsync, isPending }
}

export const useDeleteFamilyMember = patientId => {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async id => {
      const res = await fetch(
        `${baseURL}/v2/rest/patients/${patientId}/family_members/${id}.json`,
        { method: 'DELETE', headers }
      )
      if (!res.ok) {
        throw new Error(
          await parseApiError(res, 'Could not remove family member')
        )
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY(patientId) })
      queryClient.invalidateQueries({ queryKey: LINKS_QUERY_KEY(patientId) })
    },
  })

  return { mutateAsync, isPending }
}

export const RELATIONSHIP_OPTIONS = [
  { value: 'father', label: 'Father' },
  { value: 'mother', label: 'Mother' },
  { value: 'son', label: 'Son' },
  { value: 'daughter', label: 'Daughter' },
  { value: 'brother', label: 'Brother' },
  { value: 'sister', label: 'Sister' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'other', label: 'Other' },
]
